import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import api from '../lib/api';

const ALL_EVENTS = ['ticket.created', 'ticket.updated', 'ticket.reply', 'ticket.csat'];

export default function WebhooksPage() {
  const qc = useQueryClient();
  const [showNew, setShowNew] = useState(false);
  const { data = [] } = useQuery({ queryKey: ['webhooks'], queryFn: async () => (await api.get('/webhooks')).data });
  const del = useMutation({
    mutationFn: async (id: string) => api.delete(`/webhooks/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['webhooks'] }); toast.success('Deleted'); },
  });

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-semibold">Webhooks</h1><p className="text-sm text-slate-500">Outbound deliveries with HMAC signing + retries.</p></div>
        <button className="btn-primary" onClick={() => setShowNew(true)}>+ New webhook</button>
      </div>
      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr><th className="text-left px-4 py-3">URL</th><th className="text-left px-4 py-3">Events</th><th className="text-left px-4 py-3">Last delivery</th><th /></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {(data as { id: string; url: string; events: string[]; lastDeliveryAt?: string; lastStatus?: number }[]).map((w) => (
              <tr key={w.id}>
                <td className="px-4 py-3 font-mono text-xs break-all">{w.url}</td>
                <td className="px-4 py-3 text-xs">{w.events.join(', ')}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{w.lastDeliveryAt ? `${new Date(w.lastDeliveryAt).toLocaleString()} (${w.lastStatus ?? '—'})` : '—'}</td>
                <td className="px-4 py-3"><button className="btn-ghost text-rose-600" onClick={() => del.mutate(w.id)}>Delete</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showNew && <NewWebhookModal onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); qc.invalidateQueries({ queryKey: ['webhooks'] }); }} />}
    </div>
  );
}

function NewWebhookModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState<string[]>(['ticket.created']);
  const create = useMutation({
    mutationFn: async () => api.post('/webhooks', { url, events }),
    onSuccess: () => { toast.success('Created'); onCreated(); },
    onError: (e: { response?: { data?: { error?: string } } }) => toast.error(e.response?.data?.error ?? 'Failed'),
  });
  function toggle(e: string) {
    setEvents((cur) => cur.includes(e) ? cur.filter((x) => x !== e) : [...cur, e]);
  }
  return (
    <div className="fixed inset-0 bg-slate-900/40 grid place-items-center p-4 z-50">
      <div className="card w-full max-w-md p-6">
        <h2 className="text-lg font-semibold mb-4">New webhook</h2>
        <div className="space-y-3">
          <div><label className="label">URL</label><input className="input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://example.com/webhook" /></div>
          <div><label className="label">Events</label>
            <div className="space-y-1">
              {ALL_EVENTS.map((e) => <label key={e} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={events.includes(e)} onChange={() => toggle(e)} /> {e}</label>)}
            </div>
          </div>
        </div>
        <div className="flex gap-2 justify-end mt-5">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={!url || events.length === 0 || create.isPending} onClick={() => create.mutate()}>{create.isPending ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}
